import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { History, Eye } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/layout/PageHeader";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { Chip } from "@/components/kit/Chip";
import { ExportButton } from "@/components/kit/ExportButton";
import { DataTable } from "@/components/tables/DataTable";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { activityService } from "@/services/resourceServices";
import { formatRelativeTime } from "@/lib/format";

const CATEGORY_TONE = {
  clients: "blue",
  stores: "green",
  users: "amber",
  company: "neutral",
  "va-agencies": "red",
};

const CATEGORY_LABEL = {
  clients: "Clients",
  stores: "Stores",
  users: "Users",
  company: "Company",
  "va-agencies": "VA Agencies",
  orders: "Orders",
  inbound: "Inbound",
  returns: "Returns",
  inventory: "Inventory",
};

const DEFAULT_FILTERS = { search: "", category: "all", actor: "", action: "", date_from: "", date_to: "" };
const SKIP_KEYS = new Set(["id", "created_at", "updated_at", "user_id", "store_key", "assigned_va_staff_ids"]);

function scalarEntries(obj) {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj).filter(
    ([k, v]) => !SKIP_KEYS.has(k) && (v === null || ["string", "number", "boolean"].includes(typeof v))
  );
}

function ChangeDialog({ open, onOpenChange, item }) {
  const prev = item?.previous_value;
  const next = item?.new_value;
  const keys = useMemo(() => {
    const set = new Set();
    scalarEntries(prev).forEach(([k]) => set.add(k));
    scalarEntries(next).forEach(([k]) => set.add(k));
    return [...set];
  }, [prev, next]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="activity-detail-dialog">
        <DialogHeader>
          <DialogTitle>{item?.action} — {item?.target}</DialogTitle>
          <DialogDescription>
            {item?.actor} · {formatRelativeTime(item?.created_at)}
          </DialogDescription>
        </DialogHeader>
        {keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">No field-level changes were recorded for this event.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="grid grid-cols-3 gap-2 border-b border-border bg-muted/40 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Field</span>
              <span>Previous</span>
              <span>New</span>
            </div>
            <div className="divide-y divide-border">
              {keys.map((k) => {
                const p = prev?.[k];
                const n = next?.[k];
                const changed = String(p) !== String(n);
                return (
                  <div key={k} className="grid grid-cols-3 gap-2 px-3 py-2 text-sm">
                    <span className="font-medium text-foreground">{k}</span>
                    <span className="truncate text-muted-foreground">{p === null || p === undefined ? "—" : String(p)}</span>
                    <span className={changed ? "truncate font-medium text-primary" : "truncate text-muted-foreground"}>
                      {n === null || n === undefined ? "—" : String(n)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ActivityLogPage() {
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [detail, setDetail] = useState(null);

  const params = useMemo(
    () => ({
      search: applied.search,
      category: applied.category === "all" ? "" : applied.category,
      actor: applied.actor,
      action: applied.action,
      date_from: applied.date_from,
      date_to: applied.date_to,
      limit: 500,
    }),
    [applied]
  );
  const listQuery = useQuery({ queryKey: ["activity", params], queryFn: () => activityService.list(params) });
  const rows = listQuery.data?.items || [];

  const exportColumns = [
    { label: "Timestamp", get: (r) => r.created_at },
    { label: "User", get: (r) => r.actor || "System" },
    { label: "Module", get: (r) => CATEGORY_LABEL[r.category] || r.category },
    { label: "Action", key: "action" },
    { label: "Record", key: "target" },
    { label: "Previous Value", get: (r) => (r.previous_value ? JSON.stringify(r.previous_value) : "") },
    { label: "New Value", get: (r) => (r.new_value ? JSON.stringify(r.new_value) : "") },
  ];

  const columns = [
    { key: "when", header: "When", render: (r) => formatRelativeTime(r.created_at) },
    { key: "actor", header: "User", render: (r) => <span className="font-medium text-foreground">{r.actor || "System"}</span> },
    {
      key: "event",
      header: "Event",
      render: (r) => (
        <span className="text-foreground">
          <span className="text-muted-foreground">{r.action}</span>{" "}
          <span className="font-medium">{r.target}</span>
        </span>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (r) => <Chip label={CATEGORY_LABEL[r.category] || r.category} tone={CATEGORY_TONE[r.category] || "neutral"} />,
    },
    {
      key: "changes",
      header: "",
      align: "right",
      render: (r) =>
        r.previous_value || r.new_value ? (
          <button
            type="button"
            onClick={() => setDetail(r)}
            data-testid={`activity-view-${r.id}`}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-primary transition-colors hover:bg-brand-light"
          >
            <Eye className="h-4 w-4" /> View
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6" data-testid="page-activity-log">
      <PageHeader
        sticky
        title="Activity Log"
        description="A complete, searchable timeline of everything happening in your account."
        actions={<ExportButton filename="activity-log" columns={exportColumns} rows={rows} testId="activity-export" />}
      />

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="Search activity…" />
        <input value={draft.actor} onChange={(e) => setDraft((d) => ({ ...d, actor: e.target.value }))} placeholder="User"
          className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-user" />
        <Select value={draft.category} onValueChange={(v) => setDraft((d) => ({ ...d, category: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All modules</SelectItem>
            <SelectItem value="orders">Orders</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
            <SelectItem value="inventory">Inventory</SelectItem>
            <SelectItem value="returns">Returns</SelectItem>
            <SelectItem value="invoicing">Invoicing</SelectItem>
            <SelectItem value="clients">Clients</SelectItem>
            <SelectItem value="stores">Stores</SelectItem>
            <SelectItem value="users">Users</SelectItem>
            <SelectItem value="va-agencies">VA Agencies</SelectItem>
            <SelectItem value="company">Company</SelectItem>
          </SelectContent>
        </Select>
        <input value={draft.action} onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))} placeholder="Action"
          className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-action" />
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">From</label>
          <input type="date" value={draft.date_from} onChange={(e) => setDraft((d) => ({ ...d, date_from: e.target.value }))}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-date-from" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">To</label>
          <input type="date" value={draft.date_to} onChange={(e) => setDraft((d) => ({ ...d, date_to: e.target.value }))}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-date-to" />
        </div>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={8} cols={5} />
      ) : (
        <DataTable
          testId="activity-table"
          paginated
          columns={columns}
          data={rows}
          emptyState={
            <EmptyState
              icon={History}
              title="No activity recorded"
              description="As your team works, every action will be captured here for auditing."
              testId="activity-empty-state"
            />
          }
        />
      )}

      <ChangeDialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)} item={detail} />
    </div>
  );
}
